/**
 * ChatPanel — Componente de chat reutilizável (presentation-only).
 *
 * Usado em:
 * - Agent Creation Drawer (assistente de criação de agentes)
 * - Workflow AI Panel (assistente de criação/edição de workflows)
 *
 * O parent controla estado (messages) e API (onSendMessage).
 * Este componente cuida apenas da renderização e UX.
 */

import React, { useEffect, useRef, useState } from 'react';
import { FiSend } from 'react-icons/fi';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

// ============================================================================
// Types
// ============================================================================

export interface ChatMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp?: Date;
  /** Badge opcional abaixo da mensagem (ex: "3 nodes adicionados") */
  badge?: string;
}

export interface ChatPanelProps {
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  /** Título no header */
  title?: string;
  /** Subtítulo */
  subtitle?: string;
  /** Placeholder do input */
  placeholder?: string;
  /** Ícone do assistente (Feather icon name) */
  assistantIcon?: IconName;
  /** Esconder header */
  hideHeader?: boolean;
  /** Mensagem inicial quando chat está vazio */
  emptyMessage?: string;
}

// ============================================================================
// Component
// ============================================================================

export const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  onSendMessage,
  isLoading,
  title = 'Assistente IA',
  subtitle,
  placeholder = 'Descreva o que deseja...',
  assistantIcon = 'cpu',
  hideHeader = false,
  emptyMessage,
}) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    onSendMessage(trimmed);
    setInput('');
    // Re-focus input after send
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      {!hideHeader && (
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: '#8b5cf6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name={assistantIcon} className="w-3.5 h-3.5" style={{ color: '#fff' }} />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>{title}</div>
              {subtitle && <div style={{ fontSize: 11, color: '#94a3b8' }}>{subtitle}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {/* Empty state */}
        {messages.length === 0 && !isLoading && emptyMessage && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 8,
              padding: '32px 16px',
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: '#f3f0ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={assistantIcon} className="w-5 h-5" style={{ color: '#8b5cf6' }} />
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', maxWidth: 240, lineHeight: 1.5 }}>
              {emptyMessage}
            </div>
          </div>
        )}

        {/* Message bubbles */}
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            {msg.role === 'assistant' && (
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: '#8b5cf6',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name={assistantIcon} className="w-3 h-3" style={{ color: '#fff' }} />
              </div>
            )}
            <div>
              <div
                style={{
                  background: msg.role === 'user' ? '#8b5cf6' : '#fff',
                  color: msg.role === 'user' ? '#fff' : '#334155',
                  border: msg.role === 'assistant' ? '1px solid #e5e7eb' : 'none',
                  borderRadius: 12,
                  padding: '10px 14px',
                  maxWidth: 280,
                  fontSize: 13,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {msg.content}
              </div>
              {msg.badge && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 10,
                    color: '#8b5cf6',
                    fontWeight: 500,
                    paddingLeft: 4,
                  }}
                >
                  {msg.badge}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: '#8b5cf6',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={assistantIcon} className="w-3 h-3" style={{ color: '#fff' }} />
            </div>
            <div
              style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                padding: '10px 14px',
                fontSize: 13,
                color: '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span className="animate-pulse">Pensando...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isLoading}
            rows={1}
            style={{
              flex: 1,
              padding: '8px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              fontSize: 13,
              color: '#334155',
              outline: 'none',
              backgroundColor: '#fff',
              resize: 'none',
              fontFamily: 'inherit',
              lineHeight: 1.5,
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: 'none',
              background: isLoading || !input.trim() ? '#e5e7eb' : '#8b5cf6',
              color: isLoading || !input.trim() ? '#94a3b8' : '#fff',
              fontSize: 13,
              cursor: isLoading || !input.trim() ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              flexShrink: 0,
            }}
          >
            <FiSend size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
